import { useState, useCallback } from 'react';
import type { AppMode, ProblemType } from './types';
import { useSettings } from './hooks/useSettings';
import { useDailyProgress } from './hooks/useDailyProgress';
import { useGame } from './hooks/useGame';
import { CategoryGrid } from './components/home/CategoryGrid';
import { GameScreen } from './components/game/GameScreen';
import { ParentSettings } from './components/settings/ParentSettings';

export default function App() {
  const [mode, setMode] = useState<AppMode>('CHILD');
  const { settings, toggleSetting, enableSetting, toggleGroup, isGroupEnabled, isGroupPartiallyEnabled } = useSettings();
  const { dailyProgress, incrementWin } = useDailyProgress();

  const handleWin = useCallback(
    (category: ProblemType) => {
      incrementWin(category);
    },
    [incrementWin]
  );

  const handleLevelUp = useCallback(
    (nextCategory: ProblemType | null) => {
      if (nextCategory) {
        enableSetting(nextCategory);
      }
    },
    [enableSetting]
  );

  const game = useGame({
    onWin: handleWin,
    onLevelUp: handleLevelUp,
  });

  // Parent Settings Mode
  if (mode === 'PARENT') {
    return (
      <ParentSettings
        settings={settings}
        onToggle={toggleSetting}
        onToggleGroup={toggleGroup}
        isGroupEnabled={isGroupEnabled}
        isGroupPartiallyEnabled={isGroupPartiallyEnabled}
        onBack={() => setMode('CHILD')}
      />
    );
  }

  // Game Screen
  if (game.selectedCategory && game.currentProblem) {
    return (
      <GameScreen
        selectedCategory={game.selectedCategory}
        currentProblem={game.currentProblem}
        input={game.input}
        feedback={game.feedback}
        showHint={game.showHint}
        sessionWins={game.sessionWins}
        showDecimal={game.showDecimal}
        hasDecimal={game.hasDecimal}
        isFractionProblem={game.isFractionProblem}
        fractionNumerator={game.fractionNumerator}
        fractionDenominator={game.fractionDenominator}
        fractionFocus={game.fractionFocus}
        onExit={game.exitGame}
        onNumber={game.handleInput}
        onDecimal={game.handleDecimal}
        onClear={game.clearInput}
        onSubmit={game.checkAnswer}
        onFractionFocusChange={game.handleFractionFocusChange}
      />
    );
  }

  // Home Screen - Category Selection
  return (
    <CategoryGrid
      settings={settings}
      dailyProgress={dailyProgress}
      onSelectCategory={game.startGame}
      onOpenSettings={() => setMode('PARENT')}
    />
  );
}
